/**
 * The Groq chat model used by every /api/groq caller, and the response_format
 * helpers that keep its JSON output parseable.
 *
 * Groq retires models on a published schedule and the proxy passes the
 * upstream status straight through, so a decommissioned id surfaces in the
 * browser as a 404 on /api/groq. Keeping the id here means the next
 * deprecation is a one-line change. scripts/autofill-specs.cjs keeps its own
 * copy - it is a CommonJS script and cannot import this module.
 *
 * llama-3.3-70b-versatile was shut down on 2026-08-16; gpt-oss-120b is Groq's
 * recommended replacement.
 */
export const GROQ_MODEL = 'openai/gpt-oss-120b';

/**
 * gpt-oss is a reasoning model, and plain `{ type: 'json_object' }` only asks
 * it to produce syntactically valid JSON - reasoning can still bleed into the
 * content, and a reply cut short by max_tokens leaves the JSON unterminated.
 * Either way the request comes back as "failed to validate JSON".
 *
 * A strict json_schema uses constrained decoding instead, so the reply is
 * guaranteed to parse and to match the shape. Strict mode requires every
 * property to be listed in `required` and every object to set
 * `additionalProperties: false`; optional values are expressed as a union with
 * null rather than by being left out.
 */
export function jsonSchemaFormat(name: string, schema: Record<string, unknown>) {
  return { type: 'json_schema', json_schema: { name, strict: true, schema } };
}

/** One field of a component type's specs_schema. */
export interface SpecField {
  key: string;
  type: string;
  options?: string[];
}

/**
 * Turn a component type's specs_schema into a strict JSON schema, so the model
 * is constrained to the real field keys and - for select fields - to the exact
 * allowed options. Every field is nullable: "not determinable" is a valid and
 * expected answer, and the caller drops the nulls.
 */
export function specsSchema(fields: SpecField[]): Record<string, unknown> {
  const properties: Record<string, unknown> = {};
  for (const field of fields) properties[field.key] = specProperty(field);

  return {
    type: 'object',
    properties,
    required: fields.map(f => f.key),
    additionalProperties: false,
  };
}

function specProperty(field: SpecField): Record<string, unknown> {
  const options = field.options?.length ? field.options : null;

  switch (field.type) {
    case 'boolean':
      return { type: ['boolean', 'null'] };
    case 'number':
      return { type: ['number', 'null'] };
    case 'select':
      return options
        ? { type: ['string', 'null'], enum: [...options, null] }
        : { type: ['string', 'null'] };
    case 'multi_select':
      return {
        type: ['array', 'null'],
        items: options ? { type: 'string', enum: options } : { type: 'string' },
      };
    default:
      return { type: ['string', 'null'] };
  }
}
